import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MdAutoFixHigh } from 'react-icons/md';
import { useStylingAssistantContext } from '../../context/StylingAssistantContext';
import type { SATheme } from '../../types/stylingAssistant.types';

// Sub-components (reusable)
import SAHeader from '../stylingAssistant/SAHeader';
import SASearchInput from '../stylingAssistant/SASearchInput';
import SAWelcomeGrid from '../stylingAssistant/SAWelcomeGrid';
import SAChatMessage from '../stylingAssistant/SAChatMessage';
import SALoadingIndicator from '../stylingAssistant/SALoadingIndicator';

// ─── Dark theme (matches app design) ────────────────────────────────────────

const DARK_THEME: SATheme = {
  bg: '#0f0f0f',
  surface: '#141414',
  text: '#ffffff',
  textSecondary: '#9ca3af',
  textMuted: '#4b5563',
  border: 'rgba(255,255,255,0.08)',
  accent: '#EE4423',
  cardBg: '#1a1a1a',
  userMessageBg: '#2c2c2c',
  assistantMessageBg: '#1a1a1a',
};

export default function StylingAssistant() {
  const location = useLocation();

  // ─── Shared context (single source of truth) ─────────────────────────────
  const {
    sa,
    isPanelOpen,
    openPanel,
    closePanel,
    expandToFullPage,
  } = useStylingAssistantContext();

  // Focus input when opened
  useEffect(() => {
    if (isPanelOpen && sa.inputRef.current) {
      setTimeout(() => sa.inputRef.current?.focus(), 300);
    }
  }, [isPanelOpen]);

  const theme = DARK_THEME;

  // Don't render on the Style Assistant dedicated page
  const isOnStyleAssistantPage = location.pathname === '/style-assistant';
  if (isOnStyleAssistantPage) return null;

  return (
    <>
      {/* ─── Floating Chat Bubble ──────────────────────────────────────── */}
      {!isPanelOpen && (
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={openPanel}
            className="group relative w-14 h-14 rounded-2xl shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-[#EE4423]/20 cursor-pointer"
            style={{
              background: 'linear-gradient(135deg, #EE4423 0%, #ff6b35 100%)',
            }}
            aria-label="Open Styling Assistant"
          >
            <MdAutoFixHigh size={24} className="text-white" />
            {/* Pulse ring */}
            <div
              className="absolute inset-0 rounded-2xl animate-ping opacity-15"
              style={{
                background: 'linear-gradient(135deg, #EE4423 0%, #ff6b35 100%)',
              }}
            />
            {/* Label tooltip */}
            <div className="absolute right-full mr-3 px-3 py-1.5 bg-[#1a1a1a] text-white text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap border border-white/10 shadow-xl">
              Style Assistant
            </div>
          </button>
        </div>
      )}

      {/* ─── Backdrop ─────────────────────────────────────────────────── */}
      {isPanelOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[1000] transition-opacity duration-300"
          onClick={closePanel}
        />
      )}

      {/* ─── Side Panel ───────────────────────────────────────────────── */}
      {isPanelOpen && (
        <div
          className="fixed top-3 right-3 bottom-3 w-full max-w-3xl shadow-2xl flex flex-col z-[1001] transition-transform duration-300 ease-out rounded-2xl overflow-hidden"
          style={{
            backgroundColor: theme.bg,
            border: `1px solid ${theme.border}`,
          }}
        >
          {/* Panel Header — now with expand button */}
          <SAHeader
            theme={theme}
            hasMessages={sa.messages.length > 0}
            onRefresh={sa.handleRefresh}
            onClose={closePanel}
            onExpand={expandToFullPage}
            variant="popup"
          />

          {/* Content Area */}
          <div
            className="flex-1 overflow-y-auto"
            style={{ backgroundColor: theme.surface }}
          >
            {sa.messages.length === 0 ? (
              /* ─── Welcome View ──────────────────────────────────────── */
              <div className="flex flex-col h-full">
                {/* Search Input at Top */}
                <div className="px-5 py-5">
                  <SASearchInput
                    theme={theme}
                    inputRef={sa.inputRef}
                    inputValue={sa.inputValue}
                    isLoading={sa.isLoading}
                    showSuggestionsDropdown={sa.showSuggestionsDropdown}
                    searchItems={sa.searchItems}
                    onInputChange={sa.setInputValue}
                    onKeyPress={sa.handleKeyPress}
                    onSend={sa.handleSend}
                    onToggleSuggestions={() =>
                      sa.setShowSuggestionsDropdown(!sa.showSuggestionsDropdown)
                    }
                    onSuggestionClick={(suggestion) => {
                      sa.handleSuggestionClick(suggestion);
                      sa.setShowSuggestionsDropdown(false);
                    }}
                  />
                </div>

                {/* Spacer */}
                <div className="flex-1" />

                {/* Grid with Products and Suggestions */}
                <SAWelcomeGrid
                  theme={theme}
                  onSuggestionClick={sa.handleSuggestionClick}
                />
              </div>
            ) : (
              /* ─── Chat View ─────────────────────────────────────────── */
              <div className="px-5 py-5 space-y-2">
                {sa.messages.map((message) => (
                  <SAChatMessage
                    key={message.id}
                    message={message}
                    theme={theme}
                    activeMessageId={sa.activeMessageId}
                    visibleGroupIndexes={sa.visibleGroupIndexes}
                    isProgressivelyDisplaying={sa.isProgressivelyDisplaying}
                    groupRefs={sa.groupRefs}
                    carouselRefs={sa.carouselRefs}
                    onFollowUpClick={sa.handleFollowUpClick}
                    onScrollCarousel={sa.scrollCarousel}
                  />
                ))}

                {/* Loading indicator */}
                {sa.isLoading && <SALoadingIndicator theme={theme} />}

                {/* Scroll anchor */}
                <div ref={sa.messagesEndRef} />
              </div>
            )}
          </div>

          {/* ─── Bottom Input (Chat Mode) ─────────────────────────────── */}
          {sa.messages.length > 0 && (
            <div className="px-5 py-4" style={{ backgroundColor: theme.bg, borderTop: `1px solid ${theme.border}` }}>
              <SASearchInput
                theme={theme}
                inputRef={sa.inputRef}
                inputValue={sa.inputValue}
                isLoading={sa.isLoading}
                showSuggestionsDropdown={sa.showSuggestionsDropdown}
                searchItems={sa.searchItems}
                dropdownDirection="up"
                onInputChange={sa.setInputValue}
                onKeyPress={sa.handleKeyPress}
                onSend={sa.handleSend}
                onToggleSuggestions={() =>
                  sa.setShowSuggestionsDropdown(!sa.showSuggestionsDropdown)
                }
                onSuggestionClick={(suggestion) => {
                  sa.handleSuggestionClick(suggestion);
                  sa.setShowSuggestionsDropdown(false);
                }}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}
